import { authFetch } from "@/lib/api-client";
import type {
  Vendor,
  TenantVendorConnection,
  CreateConnectionPayload,
  UpdateAnswersPayload,
  ResignConnectionPayload,
  MarketplaceOrder,
  SentScriptsMap,
  CreateOrderPayload,
  UpdateOrderEtaPayload,
  UpdateOrderEtaResult,
} from "@/features/marketplace/types/marketplace.types";

// ─── Reads ─────────────────────────────────────────────────────────────────

export async function fetchVendors(tenantId: string): Promise<Vendor[]> {
  const { data } = await authFetch<Vendor[]>(
    `/marketplace/vendors?tenantId=${encodeURIComponent(tenantId)}`,
  );
  return data;
}

/** Vendor detail (packages + questions) for the connect wizard. */
export async function fetchVendorDetails(vendorId: string): Promise<Vendor> {
  const { data } = await authFetch<Vendor>(
    `/marketplace/vendors/${encodeURIComponent(vendorId)}/questions`,
  );
  return data;
}

export async function fetchConnections(
  tenantId: string,
): Promise<TenantVendorConnection[]> {
  const { data } = await authFetch<TenantVendorConnection[]>(
    `/marketplace/connections?tenantId=${encodeURIComponent(tenantId)}`,
  );
  return data;
}

// ─── Mutations ─────────────────────────────────────────────────────────────

export async function createConnection(
  payload: CreateConnectionPayload,
): Promise<TenantVendorConnection> {
  const { data } = await authFetch<TenantVendorConnection>(
    "/marketplace/connections",
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
  return data;
}

export async function updateAnswers(
  connectionId: string,
  payload: UpdateAnswersPayload,
): Promise<TenantVendorConnection> {
  const { data } = await authFetch<TenantVendorConnection>(
    `/marketplace/connections/${encodeURIComponent(connectionId)}/answers`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
    },
  );
  return data;
}

export async function resignConnection(
  connectionId: string,
  payload: ResignConnectionPayload,
): Promise<TenantVendorConnection> {
  const { data } = await authFetch<TenantVendorConnection>(
    `/marketplace/connections/${encodeURIComponent(connectionId)}/resign`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
  return data;
}

export async function disconnectVendor(
  connectionId: string,
  tenantId: string,
): Promise<void> {
  await authFetch(
    `/marketplace/connections/${encodeURIComponent(connectionId)}`,
    {
      method: "DELETE",
      body: JSON.stringify({ tenantId }),
    },
  );
}

// ─── Orders ────────────────────────────────────────────────────────────────

export async function fetchOrders(
  tenantId: string,
): Promise<MarketplaceOrder[]> {
  const { data } = await authFetch<MarketplaceOrder[]>(
    `/marketplace/orders?tenantId=${encodeURIComponent(tenantId)}`,
  );
  return data;
}

export async function fetchSentScripts(
  tenantId: string,
): Promise<SentScriptsMap> {
  const { data } = await authFetch<SentScriptsMap>(
    `/marketplace/orders/sent-scripts?tenantId=${encodeURIComponent(tenantId)}`,
  );
  return data;
}

export async function createOrder(
  payload: CreateOrderPayload,
): Promise<MarketplaceOrder> {
  const idempotencyKey = crypto.randomUUID();
  const { data } = await authFetch<MarketplaceOrder>("/marketplace/orders", {
    method: "POST",
    body: JSON.stringify({ ...payload, idempotencyKey }),
  });
  return data;
}

export interface RetryOrderResult {
  orderId: string;
  status: MarketplaceOrder["status"];
}

export async function retryOrder(
  orderId: string,
  tenantId: string,
): Promise<RetryOrderResult> {
  const { data } = await authFetch<RetryOrderResult>(
    `/marketplace/orders/${encodeURIComponent(orderId)}/retry`,
    {
      method: "POST",
      body: JSON.stringify({ tenantId }),
    },
  );
  return data;
}

export async function updateOrderEta(
  orderId: string,
  payload: UpdateOrderEtaPayload,
): Promise<UpdateOrderEtaResult> {
  const res = await authFetch(
    `/marketplace/orders/${encodeURIComponent(orderId)}/eta`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
      rawResponse: true,
    },
  );
  const json = (await res.json()) as {
    success: boolean;
    data: MarketplaceOrder;
    emailWarning?: string;
  };
  return { order: json.data, emailWarning: json.emailWarning };
}
